import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { IoClose } from 'react-icons/io5';
import { MdInfoOutline, MdNorthEast } from 'react-icons/md';
import Button from 'components/Common/Button';
import { useToast } from '@/hooks/toast';
import { useGetConcurrencySettings, useUpdateConcurrencySettings } from '@/apis/business/setting/location/hook';

export type LocationReservation = {
  locationId: number;
  inbound: number;
  outbound: number;
};

type ConcurrencySettingsDrawerProps = {
  open: boolean;
  onClose: () => void;
  onBuyMore: () => void;
};

function defaultInboundFor(includedPerLocation: number): number {
  return Math.round(Math.max(0, includedPerLocation) * 0.6);
}

function clampNonNegative(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.floor(value));
}

function SummaryCard({
  label,
  value,
  helper,
  footer
}: {
  label: string;
  value: number;
  helper?: string;
  footer?: ReactNode;
}) {
  const theme = useTheme();

  return (
    <Box
      flex={1}
      minWidth={0}
      sx={{
        background: '#fff',
        border: '1px solid #EEEEEE',
        borderRadius: '8px',
        borderLeft: `3px solid ${theme.palette.primary.main}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box px={2} pt={2} pb={1.5}>
        <Typography fontSize={14} color='#6B7280' fontWeight={600} lineHeight={1.3}>
          {label}
        </Typography>
        <Typography fontSize={28} fontWeight={700} lineHeight={1.15} mt={1.25} color='#111827'>
          {value}
        </Typography>
      </Box>

      <Box borderTop='1px solid #EEEEEE' px={2} py={1.25} minHeight={40} display='flex' alignItems='center'>
        {helper && (
          <Typography fontSize={12} color='#9CA3AF' lineHeight={1.35} fontWeight={400}>
            {helper}
          </Typography>
        )}
        {footer}
      </Box>
    </Box>
  );
}

function ReservationInput({
  value,
  onChange,
  disabled,
  ariaLabel,
  emptyWhenZero = false
}: {
  value: number;
  onChange: (next: number) => void;
  disabled?: boolean;
  ariaLabel: string;
  emptyWhenZero?: boolean;
}) {
  return (
    <TextField
      type='number'
      size='small'
      value={emptyWhenZero && value === 0 ? '' : value}
      disabled={disabled}
      placeholder={emptyWhenZero ? '0' : undefined}
      onChange={(e) => {
        const raw = e.target.value;
        onChange(raw === '' ? 0 : clampNonNegative(Number(raw)));
      }}
      inputProps={{
        min: 0,
        step: 1,
        'aria-label': ariaLabel
      }}
      sx={{
        width: 88,
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          bgcolor: '#fff',
          height: 40,
          '& fieldset': {
            borderColor: '#E5E7EB'
          },
          '&:hover fieldset': {
            borderColor: '#D1D5DB'
          },
          '&.Mui-focused fieldset': {
            borderColor: 'primary.main',
            borderWidth: 1
          }
        },
        '& input': {
          textAlign: 'center',
          py: 0,
          px: 1,
          fontSize: 14,
          fontWeight: 500,
          color: 'text.primary'
        },
        '& input[type=number]': {
          MozAppearance: 'textfield'
        },
        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
          WebkitAppearance: 'inner-spin-button',
          opacity: 1,
          height: 28,
          margin: 0
        }
      }}
    />
  );
}

export default function ConcurrencySettingsDrawer({ open, onClose, onBuyMore }: ConcurrencySettingsDrawerProps) {
  const theme = useTheme();
  const { showToast, ToastComponent } = useToast();
  const [reservations, setReservations] = useState<LocationReservation[]>([]);

  const {
    data: summary,
    isLoading,
    isError,
    refetch,
    isFetching
  } = useGetConcurrencySettings({
    enabled: open
  });
  const { mutateAsync: saveSettings, isPending: saving } = useUpdateConcurrencySettings();

  const perLocation = summary?.included_concurrency_per_location ?? 0;
  const locationCount = summary?.active_locations_count ?? reservations.length;
  const addonQty = summary?.addon_quantity ?? 0;
  const totalAvailable = summary?.total_available ?? locationCount * perLocation + addonQty;

  useEffect(() => {
    if (!open || !summary) return;

    const inboundFallback = defaultInboundFor(summary.included_concurrency_per_location);
    setReservations(
      (summary.locations || []).map((loc) => ({
        locationId: loc.id,
        inbound: loc.reserved_inbound_concurrency ?? inboundFallback,
        outbound: loc.reserved_outbound_concurrency ?? 0
      }))
    );
  }, [open, summary]);

  const reserved = useMemo(
    () => reservations.reduce((sum, row) => sum + row.inbound + row.outbound, 0),
    [reservations]
  );

  const pool = Math.max(0, totalAvailable - reserved);

  const locationNameById = useMemo(() => {
    const map = new Map<number, string>();
    (summary?.locations || []).forEach((loc) => map.set(loc.id, loc.name));
    return map;
  }, [summary?.locations]);

  const updateReservation = (locationId: number, field: 'inbound' | 'outbound', value: number) => {
    setReservations((prev) =>
      prev.map((row) => (row.locationId === locationId ? { ...row, [field]: clampNonNegative(value) } : row))
    );
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSave = async () => {
    if (!summary) return;

    for (const row of reservations) {
      const locationReserved = row.inbound + row.outbound;
      if (locationReserved > perLocation) {
        const name = locationNameById.get(row.locationId) ?? `Location ${row.locationId}`;
        showToast(
          `${name}: reserved concurrency (${locationReserved}) exceeds package allotment (${perLocation}).`,
          'error'
        );
        return;
      }
    }

    if (reserved > totalAvailable) {
      showToast(
        `Reserved concurrency (${reserved}) exceeds available capacity (${totalAvailable}). Reduce reservations or buy more.`,
        'error'
      );
      return;
    }

    try {
      await saveSettings({
        locations: reservations.map((row) => ({
          id: row.locationId,
          reserved_inbound_concurrency: Number(row.inbound) || 0,
          reserved_outbound_concurrency: Number(row.outbound) || 0
        }))
      });
      showToast('Concurrency settings saved', 'success');
      onClose();
    } catch (error: any) {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to save concurrency settings';
      showToast(typeof message === 'string' ? message : 'Failed to save concurrency settings', 'error');
    }
  };

  return (
    <>
      <Drawer
        anchor='right'
        open={open}
        onClose={handleClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: 900,
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '-8px 0 24px rgba(0,0,0,0.08)'
          }
        }}
      >
        <Box
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          px={3}
          py={2}
          borderBottom='1px solid #ECECEC'
        >
          <Typography fontSize={18} fontWeight={600} color='text.primary'>
            Concurrency Settings
          </Typography>
          <IconButton onClick={handleClose} size='small' aria-label='Close' sx={{ color: 'text.secondary' }}>
            <IoClose size={22} />
          </IconButton>
        </Box>

        <Box flex={1} overflow='auto' px={3} py={2.5}>
          {isLoading || (isFetching && !summary) ? (
            <Box display='flex' justifyContent='center' py={8}>
              <CircularProgress size={32} />
            </Box>
          ) : isError || !summary ? (
            <Stack alignItems='center' gap={1.5} py={6}>
              <Typography color='text.secondary' fontSize={14}>
                Could not load concurrency settings.
              </Typography>
              <Button variant='outlined' onClick={() => refetch()}>
                Retry
              </Button>
            </Stack>
          ) : (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} mb={3.5}>
                <SummaryCard
                  label='Total Available'
                  value={totalAvailable}
                  helper={
                    addonQty > 0
                      ? `${locationCount} Location${locationCount === 1 ? '' : 's'} × ${perLocation} + ${addonQty} add-on`
                      : `${locationCount} Location${locationCount === 1 ? '' : 's'} × ${perLocation}`
                  }
                />
                <SummaryCard label='Reserved' value={reserved} helper='Total number of reserved concurrencies' />
                <SummaryCard
                  label='Pool'
                  value={pool}
                  footer={
                    <Box
                      component='button'
                      type='button'
                      onClick={onBuyMore}
                      sx={{
                        p: 0,
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.375,
                        color: theme.palette.primary.main,
                        fontSize: 12,
                        fontWeight: 500,
                        fontFamily: 'inherit',
                        lineHeight: 1.35,
                        whiteSpace: 'nowrap',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      Buy more in Add-Ons
                      <MdNorthEast size={13} style={{ flexShrink: 0 }} />
                    </Box>
                  }
                />
              </Stack>

              <Typography fontSize={15} fontWeight={600} color='text.primary' mb={1.25}>
                Reserve per Location
              </Typography>

              <Box>
                <Table
                  size='small'
                  sx={{
                    tableLayout: 'fixed',
                    '& .MuiTableCell-root': {
                      borderColor: '#E5E7EB'
                    }
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          fontSize: 11,
                          letterSpacing: '0.04em',
                          color: 'text.secondary',
                          py: 1,
                          pl: 0,
                          borderBottom: '1px solid #E5E7EB',
                          width: '40%'
                        }}
                      >
                        LOCATION
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontWeight: 600,
                          fontSize: 11,
                          letterSpacing: '0.04em',
                          color: 'text.secondary',
                          py: 1,
                          borderBottom: '1px solid #E5E7EB',
                          width: '30%'
                        }}
                      >
                        INBOUND
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontWeight: 600,
                          fontSize: 11,
                          letterSpacing: '0.04em',
                          color: 'text.secondary',
                          py: 1,
                          pr: 0,
                          borderBottom: '1px solid #E5E7EB',
                          width: '30%'
                        }}
                      >
                        OUTBOUND
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reservations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align='center' sx={{ py: 4, pl: 0, borderBottom: 'none' }}>
                          <Typography color='text.secondary' fontSize={14}>
                            No locations to reserve yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      reservations.map((row, index) => {
                        const isLast = index === reservations.length - 1;
                        const name = locationNameById.get(row.locationId) ?? `Location ${row.locationId}`;
                        return (
                          <TableRow key={row.locationId}>
                            <TableCell
                              sx={{
                                fontWeight: 500,
                                fontSize: 14,
                                color: 'text.primary',
                                py: 1.5,
                                pl: 0,
                                borderBottom: isLast ? 'none' : '1px solid #E5E7EB'
                              }}
                            >
                              {name}
                            </TableCell>
                            <TableCell
                              align='center'
                              sx={{
                                py: 1.25,
                                borderBottom: isLast ? 'none' : '1px solid #E5E7EB'
                              }}
                            >
                              <ReservationInput
                                value={row.inbound}
                                onChange={(next) => updateReservation(row.locationId, 'inbound', next)}
                                disabled={saving}
                                ariaLabel={`${name} inbound`}
                              />
                            </TableCell>
                            <TableCell
                              align='center'
                              sx={{
                                py: 1.25,
                                pr: 0,
                                borderBottom: isLast ? 'none' : '1px solid #E5E7EB'
                              }}
                            >
                              <ReservationInput
                                value={row.outbound}
                                onChange={(next) => updateReservation(row.locationId, 'outbound', next)}
                                disabled={saving}
                                ariaLabel={`${name} outbound`}
                                emptyWhenZero
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </Box>

              <Stack direction='row' spacing={1} alignItems='flex-start' mt={2}>
                <Box
                  sx={{
                    mt: '2px',
                    flexShrink: 0,
                    color: 'text.secondary',
                    display: 'flex',
                    lineHeight: 0
                  }}
                >
                  <MdInfoOutline size={16} />
                </Box>
                <Typography fontSize={12} color='text.secondary' lineHeight={1.5}>
                  Calls use their location&apos;s inbound or outbound reservation first, then borrow from the shared
                  pool when unreserved or exceeded.
                </Typography>
              </Stack>
            </>
          )}
        </Box>

        <Stack
          direction='row'
          justifyContent='flex-end'
          gap={1.5}
          px={3}
          py={2}
          borderTop='1px solid #ECECEC'
          bgcolor='#fff'
        >
          <Button
            variant='outlined'
            color='inherit'
            onClick={handleClose}
            disabled={saving}
            sx={{
              borderColor: '#E5E7EB',
              color: 'text.primary',
              minWidth: 88,
              '&:hover': { borderColor: '#D1D5DB', bgcolor: '#F9FAFB' }
            }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleSave}
            disabled={saving || isLoading || isError || !summary || reservations.length === 0}
            sx={{ minWidth: 88 }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Stack>
      </Drawer>
      {ToastComponent}
    </>
  );
}
